import React, { useRef, useEffect } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import { gsap } from 'gsap';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export interface AnimatedTooltipProps {
  /** Tooltip content */
  children: React.ReactNode;
  /** Whether to show the tooltip */
  show: boolean;
  /** Position relative to parent (default: 'bottom') */
  position?: TooltipPosition;
  /** Distance from parent in pixels (default: 32) */
  offset?: number;
  /** Additional sx props */
  sx?: SxProps<Theme>;
}

/**
 * AnimatedTooltip - Atomic tooltip with bounce-in animation
 * 
 * Automatically animates in/out with GSAP when show prop changes.
 * 
 * Layer 1: Atomic Component
 * - Pure presentation, no domain knowledge
 * - Self-contained animation logic
 * 
 * @example
 * ```tsx
 * <div style={{ position: 'relative' }}>
 *   <button>Hover me</button>
 *   <AnimatedTooltip show={isHovered} position="bottom">
 *     Tooltip text
 *   </AnimatedTooltip>
 * </div>
 * ```
 */
export function AnimatedTooltip({
  children,
  show,
  position = 'bottom',
  offset = 32,
  sx,
}: AnimatedTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (show && tooltipRef.current) {
      gsap.fromTo(
        tooltipRef.current,
        { 
          opacity: 0, 
          y: position === 'bottom' ? -10 : position === 'top' ? 10 : 0,
          x: position === 'right' ? -10 : position === 'left' ? 10 : 0,
          scale: 0.9,
        },
        { 
          opacity: 1, 
          y: 0,
          x: 0,
          scale: 1,
          duration: 0.3,
          ease: 'back.out(1.7)',
        }
      );
    }
  }, [show, position]);

  if (!show) return null;

  const positionStyles = getPositionStyles(position, offset);

  return (
    <Box
      ref={tooltipRef}
      sx={{
        position: 'absolute',
        backgroundColor: '#0C0F24', // $color-warm-black
        color: '#FFFFFF',
        padding: '6px 12px',
        borderRadius: '4px',
        fontSize: '14px',
        whiteSpace: 'nowrap',
        zIndex: 10,
        ...positionStyles,
        '&::before': {
          content: '""',
          position: 'absolute',
          ...getArrowStyles(position),
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// Helper functions for positioning
function getPositionStyles(position: TooltipPosition, offset: number) {
  switch (position) {
    case 'bottom':
      return {
        bottom: `-${offset}px`,
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case 'top':
      return {
        top: `-${offset}px`,
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case 'left':
      return {
        left: `-${offset}px`,
        top: '50%',
        transform: 'translateY(-50%)',
      };
    case 'right':
      return {
        right: `-${offset}px`,
        top: '50%',
        transform: 'translateY(-50%)',
      };
  }
}

function getArrowStyles(position: TooltipPosition) {
  const arrowSize = '6px';
  
  switch (position) {
    case 'bottom':
      return {
        top: `-${arrowSize}`,
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: `${arrowSize} solid transparent`,
        borderRight: `${arrowSize} solid transparent`,
        borderBottom: `${arrowSize} solid #0C0F24`,
      };
    case 'top':
      return {
        bottom: `-${arrowSize}`,
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: `${arrowSize} solid transparent`,
        borderRight: `${arrowSize} solid transparent`,
        borderTop: `${arrowSize} solid #0C0F24`,
      };
    case 'left':
      return {
        right: `-${arrowSize}`,
        top: '50%',
        transform: 'translateY(-50%)',
        borderTop: `${arrowSize} solid transparent`,
        borderBottom: `${arrowSize} solid transparent`,
        borderLeft: `${arrowSize} solid #0C0F24`,
      };
    case 'right':
      return {
        left: `-${arrowSize}`,
        top: '50%',
        transform: 'translateY(-50%)',
        borderTop: `${arrowSize} solid transparent`,
        borderBottom: `${arrowSize} solid transparent`,
        borderRight: `${arrowSize} solid #0C0F24`,
      };
  }
}

export default AnimatedTooltip;
